import { googleMapsDistanceService } from '../../services/googleMapsDistanceService';
import type { Coordinates, RouteLeg } from '../../services/googleMapsDistanceService';
import {
  tenantLocalToUtcDate,
  tenantShiftedNow,
  toIanaTimezone,
} from '../../utils/tenantTime';
import { formatTimeOfDay24, formatTimeString } from '../../utils/timeUtils';
import { normalizeServiceAreaCode, isValidServiceAreaCode } from '../../utils/addressRegions';
import type { Appointment } from '../../components/timeline/types';

/**
 * Draft entered by the user in the BulkRoute modal. Coordinates may be null
 * until the address is selected from Google Places.
 */
export interface BulkStopDraft {
  id: string;
  title: string;
  addressLine1: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  latitude: number | null;
  longitude: number | null;
  durationMinutes: number;
  bufferMinutes: number;
  /** Tenant-local "HH:MM" if the user pinned this stop to a specific time. */
  fixedStartTime: string | null;
  contactId?: string;
  /** Display name for `contactId`, kept on the draft so restored drafts still show who's linked. */
  contactName?: string;
}

/**
 * The fully-placed version of a draft after optimization. Times are in
 * tenant local (HH:MM) for display and UTC Dates for API submission.
 */
export interface BulkStopPlanned {
  draft: BulkStopDraft;
  startUtc: Date;
  endUtc: Date;
  startTenantHHMM: string;
  endTenantHHMM: string;
  /** Drive minutes from the previous waypoint (origin or prior stop). */
  driveMinutesIn: number;
  driveMilesIn: number;
  isFixed: boolean;
}

export interface BulkRouteContext {
  /** Selected day in browser-local frame; only y/m/d matter. */
  date: Date;
  /** "YYYY-MM-DD" in tenant local. */
  dateString: string;
  companyTimezone: string;
  /** "HH:MM" in tenant local. */
  workingHoursStart: string;
  workingHoursEnd: string;
  /** Optional lunch break, treated as a blocker when present. */
  lunchStart?: string;
  lunchEnd?: string;
  startingCoords: Coordinates;
  startingAddress: string;
  endingCoords: Coordinates;
  endingAddress: string;
  /**
   * Existing appointments on this day - treated as immovable blockers.
   * Bulk stops must fit AROUND them.
   */
  existingAppointments: Appointment[];
  selectedTeamMemberId: string;
  /**
   * Optional "now" override expressed as a tenant-shifted Date (wall-clock
   * numbers match the tenant's clock). When omitted, derived from
   * `companyTimezone`. Lets tests pin time deterministically.
   */
  nowOverride?: Date;
  /** True when the selected day is marked off (company or user override). */
  isDayOff?: boolean;
  /** True when the selected day is a company holiday. */
  isHoliday?: boolean;
  /** Optional holiday display name (e.g. "Memorial Day"). */
  holidayName?: string | null;
  /**
   * Optional service-area config. When `mode === 'zipcode'` and `zipCodes`
   * is populated, stops with a zipCode outside the set surface a soft
   * `service_area_warning`. Other modes are ignored.
   */
  serviceArea?: {
    mode: string | null | undefined;
    /** Tenant ISO country ('US' | 'CA') — picks the postal-code key format. */
    country?: string | null;
    /** Set of normalized service-area keys (US 5-digit ZIP / CA 3-char FSA). */
    zipCodes: Set<string>;
  };
  /**
   * Optional user-supplied "soft" earliest-start time in tenant-local
   * "HH:MM" format. The optimizer uses
   * `max(workingHoursStart, preferredStartTime)` as the day's effective
   * start UNLESS a locked-time stop falls before that, in which case
   * the lock wins (the effective start drops back down to the lock,
   * clamped by `workingHoursStart`). When today + `now` is later than
   * both, `now` always wins.
   */
  preferredStartTime?: string;
  /**
   * Optional user-supplied "soft" latest-end time in tenant-local
   * "HH:MM" format. The optimizer uses
   * `min(workingHoursEnd, preferredEndTime)` as the day's effective
   * end UNLESS a locked-time stop runs past it (start + duration +
   * buffer), in which case the lock wins (the effective end extends to
   * the lock, capped by `workingHoursEnd`).
   */
  preferredEndTime?: string;
}

export interface BulkRouteResult {
  /** Only the bulk-route stops, in the order they were placed. */
  orderedStops: BulkStopPlanned[];
  /** Existing-day items rendered in the review for context (not created). */
  contextAnchors: ContextAnchor[];
  totalMiles: number;
  totalDriveMinutes: number;
  totalAtStopMinutes: number;
  totalBufferMinutes: number;
  /**
   * Wall-clock time the day finishes: the last stop (or a later existing
   * appointment) plus its buffer and the drive home.
   */
  endingTimeHHMM: string;
  /** Drive home leg from wherever the day ends back to the ending location. */
  finalDriveMinutes: number;
  finalDriveMiles: number;
}

export interface ContextAnchor {
  kind: 'existing_appointment' | 'lunch';
  /** Source `Appointment.id` for `existing_appointment` anchors. */
  appointmentId?: string;
  startTenantHHMM: string;
  endTenantHHMM: string;
  label: string;
  /** Pre-buffer (drive cushion) attached to this anchor, in minutes. */
  bufferBeforeMinutes?: number;
  /** Post-buffer (drive cushion) attached to this anchor, in minutes. */
  bufferAfterMinutes?: number;
}

export type ConflictKind =
  | 'no_stops'
  | 'no_starting_location'
  | 'no_ending_location'
  | 'missing_title'
  | 'missing_duration'
  | 'address_invalid'
  | 'fixed_time_outside_hours'
  | 'fixed_time_in_past'
  | 'fixed_time_overlap_existing'
  | 'fixed_time_overlap_other_stop'
  | 'fixed_time_overlap_lunch'
  | 'fixed_time_unreachable'
  | 'day_in_past'
  | 'day_off'
  | 'day_is_holiday'
  | 'day_overflow'
  | 'maps_unavailable'
  | 'service_area_warning'
  | 'lock_anchored_first';

/**
 * Conflict severity. `error` blocks Routemize. `warning` lets the user
 * proceed but is surfaced prominently in the UI so they can self-correct.
 */
export type ConflictSeverity = 'error' | 'warning';

export interface BulkRouteConflict {
  stopId: string | null;
  kind: ConflictKind;
  message: string;
  /** Optional one-click action label / hint shown next to the message. */
  fixHint?: string;
  /** Defaults to `error` when omitted. */
  severity?: ConflictSeverity;
}

/**
 * Convenience: returns true when at least one conflict is a hard `error`.
 * Warnings (e.g. service-area) shouldn't block submission.
 */
export function hasBlockingConflict(conflicts: BulkRouteConflict[]): boolean {
  return conflicts.some((c) => (c.severity ?? 'error') === 'error');
}

/** Synthetic timeline rows that must not count as scheduling blockers. */
const SCHEDULING_BLOCKER_SKIP_IDS = new Set([
  'virtual-start',
  'virtual-end',
  'lunch-break',
]);

/**
 * True when an existing-day item should block bulk-route lock times and
 * consume day budget. Cancelled / no-show and synthetic rows are ignored.
 */
export function isSchedulingBlockerAppointment(appt: Appointment): boolean {
  if (SCHEDULING_BLOCKER_SKIP_IDS.has(appt.id)) return false;
  if (appt.status === 'cancelled' || appt.status === 'no-show') return false;
  if (!(appt.startDateTime instanceof Date) || isNaN(appt.startDateTime.getTime())) {
    return false;
  }
  if (!(appt.endDateTime instanceof Date) || isNaN(appt.endDateTime.getTime())) {
    return false;
  }
  return true;
}

function getExistingAppointmentBlockerMinutes(
  appt: Appointment,
  tenantTimezone: string | null | undefined
): { startMinutes: number; endMinutes: number } {
  const pre = appt.preBufferMinutes ?? 0;
  const post = appt.postBufferMinutes ?? 0;
  return {
    startMinutes:
      appointmentDateToTenantMinutes(appt.startDateTime, tenantTimezone) - pre,
    endMinutes:
      appointmentDateToTenantMinutes(appt.endDateTime, tenantTimezone) + post,
  };
}

/**
 * Live fixed-time validation: overlaps with existing appointments, lunch,
 * and duplicate locks across bulk stops. Surfaces inline before Routemize.
 */
export function previewFixedTimeConflicts(
  stops: BulkStopDraft[],
  ctx: Pick<
    BulkRouteContext,
    | 'existingAppointments'
    | 'companyTimezone'
    | 'lunchStart'
    | 'lunchEnd'
  >
): BulkRouteConflict[] {
  const conflicts: BulkRouteConflict[] = [];
  const blockers = ctx.existingAppointments.filter(isSchedulingBlockerAppointment);

  for (const stop of stops) {
    if (!stop.fixedStartTime) continue;
    const start = hhmmToMinutes(stop.fixedStartTime);
    const end = start + stop.durationMinutes + stop.bufferMinutes;

    if (
      ctx.lunchStart &&
      ctx.lunchEnd &&
      intervalsOverlap(
        start,
        end,
        hhmmToMinutes(ctx.lunchStart),
        hhmmToMinutes(ctx.lunchEnd)
      )
    ) {
      conflicts.push({
        stopId: stop.id,
        kind: 'fixed_time_overlap_lunch',
        message: `This start time overlaps lunch (${formatTimeString(ctx.lunchStart)} – ${formatTimeString(ctx.lunchEnd)}).`,
        fixHint: 'Make it flexible',
      });
    }

    for (const appt of blockers) {
      const { startMinutes: aStart, endMinutes: aEnd } =
        getExistingAppointmentBlockerMinutes(appt, ctx.companyTimezone);
      if (intervalsOverlap(start, end, aStart, aEnd)) {
        conflicts.push({
          stopId: stop.id,
          kind: 'fixed_time_overlap_existing',
          message: `This start time overlaps "${appt.customerName || appt.type || 'an existing appointment'}".`,
          fixHint: 'Make it flexible or pick a free time',
        });
      }
    }
  }

  const fixed = stops.filter((s) => s.fixedStartTime);
  for (let i = 0; i < fixed.length; i++) {
    for (let j = i + 1; j < fixed.length; j++) {
      const a = fixed[i];
      const b = fixed[j];
      const aStart = hhmmToMinutes(a.fixedStartTime!);
      const aEnd = aStart + a.durationMinutes + a.bufferMinutes;
      const bStart = hhmmToMinutes(b.fixedStartTime!);
      const bEnd = bStart + b.durationMinutes + b.bufferMinutes;
      if (intervalsOverlap(aStart, aEnd, bStart, bEnd)) {
        const sameStart = aStart === bStart;
        conflicts.push({
          stopId: a.id,
          kind: 'fixed_time_overlap_other_stop',
          message: sameStart
            ? `"${b.title || 'Another stop'}" already starts at this time.`
            : `This start time overlaps "${b.title || 'another fixed-time stop'}".`,
          fixHint: sameStart ? 'Pick a different time' : 'Make one of them flexible',
        });
        conflicts.push({
          stopId: b.id,
          kind: 'fixed_time_overlap_other_stop',
          message: sameStart
            ? `"${a.title || 'Another stop'}" already starts at this time.`
            : `This start time overlaps "${a.title || 'another fixed-time stop'}".`,
          fixHint: sameStart ? 'Pick a different time' : 'Make one of them flexible',
        });
      }
    }
  }

  return conflicts;
}

/**
 * Lock-time picker slots ("HH:MM") that would overlap another bulk stop's
 * lock or an existing appointment / lunch for this stop's duration + buffer.
 */
export function buildDisabledLockTimesForStop(
  stop: BulkStopDraft,
  allStops: BulkStopDraft[],
  ctx: Pick<
    BulkRouteContext,
    | 'existingAppointments'
    | 'companyTimezone'
    | 'lunchStart'
    | 'lunchEnd'
  >,
  window?: { minMinutes: number; maxMinutes: number }
): Map<string, 'in_use' | 'busy'> {
  const disabled = new Map<string, 'in_use' | 'busy'>();
  const min = window?.minMinutes ?? 0;
  const max = window?.maxMinutes ?? 24 * 60;
  const lockSpan = stop.durationMinutes + stop.bufferMinutes;
  const blockers = ctx.existingAppointments.filter(isSchedulingBlockerAppointment);

  for (const other of allStops) {
    if (other.id !== stop.id && other.fixedStartTime) {
      disabled.set(other.fixedStartTime, 'in_use');
    }
  }

  for (let t = min; t <= max; t += 5) {
    const hhmm = minutesToHHMM(t);
    if (disabled.has(hhmm)) continue;
    const lockEnd = t + lockSpan;

    if (
      ctx.lunchStart &&
      ctx.lunchEnd &&
      intervalsOverlap(
        t,
        lockEnd,
        hhmmToMinutes(ctx.lunchStart),
        hhmmToMinutes(ctx.lunchEnd)
      )
    ) {
      disabled.set(hhmm, 'busy');
      continue;
    }

    for (const appt of blockers) {
      const { startMinutes: aStart, endMinutes: aEnd } =
        getExistingAppointmentBlockerMinutes(appt, ctx.companyTimezone);
      if (intervalsOverlap(t, lockEnd, aStart, aEnd)) {
        disabled.set(hhmm, 'busy');
        break;
      }
    }
  }

  return disabled;
}

/**
 * Cheap "live" check for the unreachable-lock conflict so the modal
 * can surface the inline row error AS SOON AS the user creates the
 * impossibility - without waiting for them to click Routemize. Only
 * inspects stops with both fixed time AND geocoded coordinates;
 * mirrors the same pair-by-pair logic in `preValidate`.
 */
export function previewUnreachableLockedPairs(
  stops: BulkStopDraft[]
): BulkRouteConflict[] {
  const conflicts: BulkRouteConflict[] = [];
  const fixed = stops
    .filter(
      (s) => s.fixedStartTime && s.latitude != null && s.longitude != null
    )
    .sort(
      (a, b) =>
        hhmmToMinutes(a.fixedStartTime!) - hhmmToMinutes(b.fixedStartTime!)
    );
  for (let i = 0; i < fixed.length - 1; i++) {
    const a = fixed[i];
    const b = fixed[i + 1];
    const aStart = hhmmToMinutes(a.fixedStartTime!);
    const aEnd = aStart + a.durationMinutes + a.bufferMinutes;
    const bStart = hhmmToMinutes(b.fixedStartTime!);
    const gapMinutes = bStart - aEnd;
    if (gapMinutes < 0) continue;
    const miles =
      haversineMiles(
        { lat: a.latitude!, lng: a.longitude! },
        { lat: b.latitude!, lng: b.longitude! }
      ) * HAVERSINE_DRIVING_MULTIPLIER;
    const requiredDriveMinutes = estimateDriveMinutesFromMiles(miles);
    if (requiredDriveMinutes > gapMinutes + 1) {
      const requiredLabel = formatMinutesAsHrMinPure(
        Math.ceil(requiredDriveMinutes)
      );
      const gapLabel =
        gapMinutes <= 0
          ? 'no time'
          : `only ${formatMinutesAsHrMinPure(gapMinutes)}`;
      conflicts.push({
        stopId: a.id,
        kind: 'fixed_time_unreachable',
        message: `Can't reach "${b.title || 'the next fixed-time stop'}" in time - it's about ${requiredLabel} away but you've left ${gapLabel} between them.`,
        fixHint: 'Make one flexible or move it later',
      });
      conflicts.push({
        stopId: b.id,
        kind: 'fixed_time_unreachable',
        message: `Starts too soon after "${a.title || 'the previous fixed-time stop'}" - it's about ${requiredLabel} away but you've left ${gapLabel} between them.`,
        fixHint: 'Make one flexible or move it later',
      });
    }
  }
  return conflicts;
}

/**
 * A relaxed-constraint alternative produced when the user's preferred
 * schedule window forced the optimizer to bump stops past a locked time.
 * Surfaced on the review step so users can one-click swap to it.
 */
export interface BulkRouteAlternative {
  /**
   * Suggested earliest start time in tenant-local "HH:MM". `null`
   * means "use workday opening" (no preferred start).
   */
  suggestedStartTime: string | null;
  /** Mirror for the latest end time. */
  suggestedEndTime: string | null;
  /** The alternative-route result the user can swap to. */
  result: BulkRouteResult;
  /**
   * How much driving the alternative saves vs the primary. Always
   * positive when the alternative is offered (we only return one when
   * it's materially better).
   */
  savings: {
    miles: number;
    driveMinutes: number;
  };
}

const MIN_PER_HOUR = 60;
const MPH_DRIVE_ESTIMATE = 35;
const HAVERSINE_DRIVING_MULTIPLIER = 1.3;

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * MIN_PER_HOUR + m;
}

/**
 * Returns a `Date` (a UTC instant) as a tenant-local "HH:MM" string. Falls
 * back to browser-local hours/minutes when `tenantTimezone` is missing or
 * unknown so existing behavior is preserved. This is required when reading
 * times off `Appointment.startDateTime` / `endDateTime` because those are
 * real UTC `Date`s — calling `.getHours()` directly returns browser-local
 * numbers, which shift the display when an admin in one timezone views a
 * tenant in another.
 */
function appointmentDateToTenantHHMM(
  date: Date,
  tenantTimezone: string | null | undefined
): string {
  const iana = tenantTimezone ? toIanaTimezone(tenantTimezone) : undefined;
  return formatTimeOfDay24(date, iana);
}

function appointmentDateToTenantMinutes(
  date: Date,
  tenantTimezone: string | null | undefined
): number {
  return hhmmToMinutes(appointmentDateToTenantHHMM(date, tenantTimezone));
}

function minutesToHHMM(totalMinutes: number): string {
  const wrapped = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(wrapped / MIN_PER_HOUR);
  const m = Math.round(wrapped % MIN_PER_HOUR);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Round a minutes-from-midnight value UP to the nearest 5-minute boundary.
 * Times already on a boundary are returned unchanged. Used so the rendered
 * (and saved) appointment times always sit on tidy :00 / :05 / :10 / …
 * marks even when Google Directions hands back fractional minute values.
 */
function roundUpToNearest5(minutes: number): number {
  return Math.ceil(minutes / 5) * 5;
}

/**
 * Returns true if the bulk-route target date matches the tenant-now date.
 * Both dates must be compared on tenant-local wall clock so impersonation
 * across timezones doesn't accidentally treat tomorrow as today.
 */
function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Compute the earliest start minute for the day. Considers (in order):
 *   1. `workingHoursStart` - the hard floor (we never go before work)
 *   2. `preferredStartTime` - optional soft floor set by the user
 *   3. Any locked-time stop in `stops` - if it falls BEFORE the
 *      preferred floor, we drop the floor down to include it (because
 *      explicit locks always win), but never below `workingHoursStart`
 *   4. "Today" only: clamps to `now` rounded up to the next 5-minute
 *      boundary so the user can't schedule into the past
 */
export function earliestStartMinutes(
  ctx: BulkRouteContext,
  stops?: BulkStopDraft[]
): number {
  const workdayStart = hhmmToMinutes(ctx.workingHoursStart);
  let floor = workdayStart;
  if (ctx.preferredStartTime) {
    floor = Math.max(workdayStart, hhmmToMinutes(ctx.preferredStartTime));
  }
  // Locked stops earlier than the preferred floor lower it, capped at
  // the workday start (so a user-typed lock at 5 AM in a 7-9 workday
  // doesn't accidentally extend the day into the small hours).
  if (stops && stops.length > 0) {
    const earliestLocked = stops
      .filter((s) => s.fixedStartTime)
      .map((s) => hhmmToMinutes(s.fixedStartTime!));
    if (earliestLocked.length > 0) {
      const earliest = Math.min(...earliestLocked);
      if (earliest < floor && earliest >= workdayStart) floor = earliest;
    }
  }
  const now = ctx.nowOverride ?? tenantShiftedNow(ctx.companyTimezone);
  if (!isSameDay(ctx.date, now)) return floor;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  return Math.max(floor, roundUpToNearest5(nowMinutes));
}

/**
 * Compute the latest end minute for the day. Considers (in order):
 *   1. `workingHoursEnd` - the hard ceiling (we never go past work)
 *   2. `preferredEndTime` - optional soft ceiling set by the user
 *   3. Any locked-time stop in `stops` whose end (start + duration +
 *      buffer) runs PAST the preferred ceiling - we extend the ceiling
 *      up to include it (locks always win), but never past
 *      `workingHoursEnd`
 */
export function latestEndMinutes(
  ctx: BulkRouteContext,
  stops?: BulkStopDraft[]
): number {
  const workdayEnd = hhmmToMinutes(ctx.workingHoursEnd);
  let ceiling = workdayEnd;
  if (ctx.preferredEndTime) {
    ceiling = Math.min(workdayEnd, hhmmToMinutes(ctx.preferredEndTime));
  }
  if (stops && stops.length > 0) {
    const latestLocked = stops
      .filter((s) => s.fixedStartTime)
      .map(
        (s) =>
          hhmmToMinutes(s.fixedStartTime!) +
          s.durationMinutes +
          s.bufferMinutes
      );
    if (latestLocked.length > 0) {
      const latest = Math.max(...latestLocked);
      if (latest > ceiling && latest <= workdayEnd) ceiling = latest;
    }
  }
  return ceiling;
}

function haversineMiles(a: Coordinates, b: Coordinates): number {
  const toRad = (x: number) => (x * Math.PI) / 180;
  const R = 3958.8;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const aHav =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(aHav));
}

function estimateDriveMinutesFromMiles(miles: number): number {
  return (miles / MPH_DRIVE_ESTIMATE) * MIN_PER_HOUR;
}

/**
 * A blocker on the day timeline. Coordinates are used as the entry/exit
 * points of the blocker so we can route flexible stops around it.
 */
interface DayBlocker {
  /** Tenant-local minutes-from-midnight. */
  startMinutes: number;
  endMinutes: number;
  /** Where the user enters this blocker (driving INTO it). */
  startCoords: Coordinates | null;
  /** Where the user exits this blocker (driving OUT of it). */
  endCoords: Coordinates | null;
  source: 'existing_appointment' | 'fixed_stop' | 'lunch';
  label: string;
  /** Original draft for fixed stops so we can place it in the result. */
  fixedDraft?: BulkStopDraft;
}

/**
 * A free segment between blockers. Flexible stops are placed inside.
 */
interface DaySegment {
  startMinutes: number;
  endMinutes: number;
  startCoords: Coordinates;
  endCoords: Coordinates;
  /** Whether the segment ends because of a blocker (vs end-of-day). */
  endsAtBlocker: boolean;
}

function appointmentToBlocker(
  appt: Appointment,
  tenantTimezone: string | null | undefined,
  priorRoutingCoords: Coordinates
): DayBlocker {
  // `startDateTime` / `endDateTime` are real UTC `Date`s. Read their
  // wall-clock numbers in the TENANT timezone so blockers line up with
  // what the timeline shows — `.getHours()` would otherwise return
  // browser-local values and shift the blocker when an admin in one
  // timezone views a tenant in another.
  // Honor pre/post booking-form buffers so bulk stops can't be packed flush
  // against a customer appointment without the configured drive cushion.
  const pre = appt.preBufferMinutes ?? 0;
  const post = appt.postBufferMinutes ?? 0;
  const startMinutes =
    appointmentDateToTenantMinutes(appt.startDateTime, tenantTimezone) - pre;
  const endMinutes =
    appointmentDateToTenantMinutes(appt.endDateTime, tenantTimezone) + post;
  const coords =
    appt.latitude != null && appt.longitude != null
      ? { lat: appt.latitude, lng: appt.longitude }
      : priorRoutingCoords;
  return {
    startMinutes,
    endMinutes,
    startCoords: coords,
    endCoords: coords,
    source: 'existing_appointment',
    label: appt.customerName || appt.type || 'Existing appointment',
  };
}

/**
 * Existing appointments and lunch as blockers, in time order. A null
 * location means the user stays wherever they already are (lunch,
 * virtual events).
 */
function contextBlockers(ctx: BulkRouteContext): DayBlocker[] {
  const blockers = ctx.existingAppointments
    .filter(isSchedulingBlockerAppointment)
    .map((appt): DayBlocker => {
      const { startMinutes, endMinutes } = getExistingAppointmentBlockerMinutes(
        appt,
        ctx.companyTimezone
      );
      const coords =
        appt.latitude != null && appt.longitude != null
          ? { lat: appt.latitude, lng: appt.longitude }
          : null;
      return {
        startMinutes,
        endMinutes,
        startCoords: coords,
        endCoords: coords,
        source: 'existing_appointment',
        label: appt.customerName || appt.type || 'Existing appointment',
      };
    });
  if (ctx.lunchStart && ctx.lunchEnd) {
    const lunchAppt = ctx.existingAppointments.find((a) => a.id === 'lunch-break');
    const coords =
      lunchAppt?.latitude != null && lunchAppt?.longitude != null
        ? { lat: lunchAppt.latitude, lng: lunchAppt.longitude }
        : null;
    blockers.push({
      startMinutes: hhmmToMinutes(ctx.lunchStart),
      endMinutes: hhmmToMinutes(ctx.lunchEnd),
      startCoords: coords,
      endCoords: coords,
      source: 'lunch',
      label: 'Lunch',
    });
  }
  return blockers.sort((a, b) => a.startMinutes - b.startMinutes);
}

function fixedStopToBlocker(
  stop: BulkStopDraft,
  priorRoutingCoords: Coordinates
): DayBlocker {
  const startMinutes = hhmmToMinutes(stop.fixedStartTime!);
  const endMinutes = startMinutes + stop.durationMinutes + stop.bufferMinutes;
  const coords =
    stop.latitude != null && stop.longitude != null
      ? { lat: stop.latitude, lng: stop.longitude }
      : priorRoutingCoords;
  return {
    startMinutes,
    endMinutes,
    startCoords: coords,
    endCoords: coords,
    source: 'fixed_stop',
    label: stop.title || 'Fixed-time stop',
    fixedDraft: stop,
  };
}

/**
 * Haversine-based cost to place one flexible stop inside a segment,
 * including mandatory trailing drive when the segment ends at a blocker.
 */
function estimateFlexibleStopSegmentCost(
  stop: BulkStopDraft,
  segment: DaySegment
): number {
  if (stop.latitude == null || stop.longitude == null) return Infinity;
  const stopCoord = { lat: stop.latitude, lng: stop.longitude };
  const inMiles =
    haversineMiles(segment.startCoords, stopCoord) *
    HAVERSINE_DRIVING_MULTIPLIER;
  const outMiles =
    haversineMiles(stopCoord, segment.endCoords) *
    HAVERSINE_DRIVING_MULTIPLIER;
  const outWeight = segment.endsAtBlocker ? 1.0 : 0.4;
  return (
    estimateDriveMinutesFromMiles(inMiles) +
    stop.durationMinutes +
    stop.bufferMinutes +
    estimateDriveMinutesFromMiles(outMiles) * outWeight
  );
}

function estimateTrailingDriveToSegmentEnd(
  stopCoord: Coordinates,
  segment: DaySegment
): number {
  const trailingMiles =
    haversineMiles(stopCoord, segment.endCoords) *
    HAVERSINE_DRIVING_MULTIPLIER;
  return estimateDriveMinutesFromMiles(trailingMiles);
}

function intervalsOverlap(
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number
): boolean {
  return aStart < bEnd && bStart < aEnd;
}

/**
 * Build the chronological list of segments where flexible stops can be placed.
 */
function buildSegments(
  ctx: BulkRouteContext,
  blockers: DayBlocker[],
  stops: BulkStopDraft[]
): DaySegment[] {
  const dayStart = earliestStartMinutes(ctx, stops);
  const dayEnd = latestEndMinutes(ctx, stops);
  const sorted = [...blockers].sort((a, b) => a.startMinutes - b.startMinutes);

  const segments: DaySegment[] = [];
  let cursor = dayStart;
  let cursorCoords: Coordinates = ctx.startingCoords;

  for (const blocker of sorted) {
    if (blocker.startMinutes > cursor) {
      segments.push({
        startMinutes: cursor,
        endMinutes: blocker.startMinutes,
        startCoords: cursorCoords,
        endCoords: blocker.startCoords ?? cursorCoords,
        endsAtBlocker: true,
      });
    }
    cursor = Math.max(cursor, blocker.endMinutes);
    cursorCoords = blocker.endCoords ?? cursorCoords;
  }

  if (cursor < dayEnd) {
    segments.push({
      startMinutes: cursor,
      endMinutes: dayEnd,
      startCoords: cursorCoords,
      endCoords: ctx.endingCoords,
      endsAtBlocker: false,
    });
  }

  return segments;
}

/**
 * Greedy last-fit: assigns each flexible stop to the latest segment that
 * can plausibly hold it (Haversine-estimated). Preferring later gaps keeps
 * bulk stops out of the tight window before an existing appointment when
 * there is plenty of free time later in the day. For empty days (one big
 * segment) this collapses to "everything in segment 0".
 *
 * Also returns a list of "blocker-segment rejections" - flexible stops
 * we couldn't fit into a segment that ends at a hard blocker (locked
 * stop, existing appointment, lunch). This drives the post-optimize
 * warning explaining why a locked stop ended up first / why some stops
 * got pushed past a blocker.
 */
function distributeFlexibleStops(
  flexible: BulkStopDraft[],
  segments: DaySegment[]
): {
  perSegment: BulkStopDraft[][];
  unfit: BulkStopDraft[];
  blockerSegmentRejections: { stop: BulkStopDraft; segmentIndex: number }[];
} {
  const perSegment: BulkStopDraft[][] = segments.map(() => []);
  const unfit: BulkStopDraft[] = [];
  const blockerSegmentRejections: {
    stop: BulkStopDraft;
    segmentIndex: number;
  }[] = [];
  const segmentRunningBudget = segments.map(
    (s) => s.endMinutes - s.startMinutes
  );

  for (const stop of flexible) {
    if (stop.latitude == null || stop.longitude == null) {
      unfit.push(stop);
      continue;
    }
    let bestSegment = -1;
    let bestCost = 0;
    for (let i = 0; i < segments.length; i++) {
      const estCost = estimateFlexibleStopSegmentCost(stop, segments[i]);
      if (estCost <= segmentRunningBudget[i]) {
        bestSegment = i;
        bestCost = estCost;
      } else if (segments[i].endsAtBlocker) {
        // Track that this stop couldn't fit in front of the blocker.
        // Used post-optimize to explain why a locked stop ended up
        // first if all flexible stops got pushed past it.
        blockerSegmentRejections.push({ stop, segmentIndex: i });
      }
    }
    if (bestSegment >= 0) {
      perSegment[bestSegment].push(stop);
      segmentRunningBudget[bestSegment] -= bestCost;
    } else {
      unfit.push(stop);
    }
  }

  return { perSegment, unfit, blockerSegmentRejections };
}

/**
 * Run Google Directions to optimize the order of stops within a single
 * segment. For a single stop, returns the trivial result. Falls back to
 * straight-through order if Directions fails (so we still produce a plan).
 */
async function optimizeSegmentOrder(
  segment: DaySegment,
  stops: BulkStopDraft[]
): Promise<{ orderedStops: BulkStopDraft[]; legs: { miles: number; minutes: number }[]; finalLegMiles: number; finalLegMinutes: number; mapsAvailable: boolean }> {
  if (stops.length === 0) {
    const directMiles =
      haversineMiles(segment.startCoords, segment.endCoords) *
      HAVERSINE_DRIVING_MULTIPLIER;
    return {
      orderedStops: [],
      legs: [],
      finalLegMiles: directMiles,
      finalLegMinutes: estimateDriveMinutesFromMiles(directMiles),
      mapsAvailable: false,
    };
  }

  const waypoints: Coordinates[] = stops.map((s) => ({
    lat: s.latitude!,
    lng: s.longitude!,
  }));

  try {
    const optimized = await googleMapsDistanceService.calculateOptimizedRoute(
      segment.startCoords,
      segment.endCoords,
      waypoints
    );

    if (optimized && optimized.optimizedLegs.length === stops.length + 1) {
      const ordered = optimized.waypointOrder.map((i: number) => stops[i]);
      const legs = optimized.optimizedLegs
        .slice(0, stops.length)
        .map((l: RouteLeg) => ({
          miles: l.distance,
          minutes: l.duration,
        }));
      const finalLeg = optimized.optimizedLegs[stops.length];
      return {
        orderedStops: ordered,
        legs,
        finalLegMiles: finalLeg.distance,
        finalLegMinutes: finalLeg.duration,
        mapsAvailable: true,
      };
    }
  } catch {
    // Fall through to Haversine fallback below.
  }

  // Fallback: keep user-entered order, estimate via Haversine.
  let prev = segment.startCoords;
  const legs = stops.map((s) => {
    const next = { lat: s.latitude!, lng: s.longitude! };
    const miles = haversineMiles(prev, next) * HAVERSINE_DRIVING_MULTIPLIER;
    prev = next;
    return { miles, minutes: estimateDriveMinutesFromMiles(miles) };
  });
  const finalMiles =
    haversineMiles(prev, segment.endCoords) * HAVERSINE_DRIVING_MULTIPLIER;

  return {
    orderedStops: [...stops],
    legs,
    finalLegMiles: finalMiles,
    finalLegMinutes: estimateDriveMinutesFromMiles(finalMiles),
    mapsAvailable: false,
  };
}

/**
 * Walk a segment timeline placing stops sequentially. Returns the placed
 * stops with assigned times, plus any that overflowed past the segment end.
 */
function walkSegment(
  ctx: BulkRouteContext,
  segment: DaySegment,
  ordered: BulkStopDraft[],
  legs: { miles: number; minutes: number }[]
): { placed: BulkStopPlanned[]; overflow: BulkStopDraft[]; segmentMiles: number } {
  const placed: BulkStopPlanned[] = [];
  const overflow: BulkStopDraft[] = [];
  let cursor = segment.startMinutes;
  let segmentMiles = 0;

  for (let i = 0; i < ordered.length; i++) {
    const draft = ordered[i];
    const leg = legs[i] ?? { miles: 0, minutes: 0 };
    // Round the calculated arrival up to the next :00/:05 mark so the
    // rendered start sits on a tidy boundary. The cushion absorbed by
    // rounding becomes a small slack buffer between stops.
    const arrival = roundUpToNearest5(cursor + leg.minutes);
    const departure = arrival + draft.durationMinutes + draft.bufferMinutes;
    const stopCoord = { lat: draft.latitude!, lng: draft.longitude! };
    // When the segment ends at a blocker, reserve trailing drive time so
    // we don't place a stop that can't physically reach the blocker.
    const placementDeadline =
      segment.endMinutes -
      (segment.endsAtBlocker
        ? estimateTrailingDriveToSegmentEnd(stopCoord, segment)
        : 0);

    if (departure > placementDeadline) {
      overflow.push(draft, ...ordered.slice(i + 1));
      break;
    }

    const startTenantHHMM = minutesToHHMM(arrival);
    const endTenantHHMM = minutesToHHMM(arrival + draft.durationMinutes);
    const startUtc = tenantLocalToUtcDate(
      ctx.dateString,
      startTenantHHMM,
      ctx.companyTimezone
    );
    const endUtc = tenantLocalToUtcDate(
      ctx.dateString,
      endTenantHHMM,
      ctx.companyTimezone
    );

    placed.push({
      draft,
      startUtc,
      endUtc,
      startTenantHHMM,
      endTenantHHMM,
      driveMinutesIn: leg.minutes,
      driveMilesIn: leg.miles,
      isFixed: false,
    });
    segmentMiles += leg.miles;
    cursor = departure;
  }

  // Post-walk validation: when this segment ends at a blocker (locked
  // stop, existing appointment, lunch), the LAST placed stop must
  // physically be able to drive to the blocker before it starts. The
  // greedy distribute step already accounts for this with full
  // trailing-drive weight, but Google Directions can return higher
  // numbers than Haversine in dense-traffic areas, so we re-check
  // here as defense-in-depth and pop trailing stops that can't make it.
  if (segment.endsAtBlocker && placed.length > 0) {
    while (placed.length > 0) {
      const last = placed[placed.length - 1];
      const lastDeparture =
        hhmmToMinutes(last.endTenantHHMM) + last.draft.bufferMinutes;
      const lastCoords = {
        lat: last.draft.latitude!,
        lng: last.draft.longitude!,
      };
      const trailingMiles =
        haversineMiles(lastCoords, segment.endCoords) *
        HAVERSINE_DRIVING_MULTIPLIER;
      const trailingMinutes = estimateDriveMinutesFromMiles(trailingMiles);
      if (lastDeparture + trailingMinutes <= segment.endMinutes) break;
      const popped = placed.pop()!;
      overflow.push(popped.draft);
      segmentMiles -= popped.driveMilesIn;
    }
  }

  return { placed, overflow, segmentMiles };
}

/**
 * Build the result for a fixed-time stop. We don't optimize anything for
 * these - the user pinned the time, so we just convert to the result shape.
 */
function placedFromFixedDraft(
  ctx: BulkRouteContext,
  draft: BulkStopDraft,
  driveMinutesIn: number,
  driveMilesIn: number
): BulkStopPlanned {
  const startMinutes = hhmmToMinutes(draft.fixedStartTime!);
  const startTenantHHMM = minutesToHHMM(startMinutes);
  const endTenantHHMM = minutesToHHMM(startMinutes + draft.durationMinutes);
  return {
    draft,
    startUtc: tenantLocalToUtcDate(
      ctx.dateString,
      startTenantHHMM,
      ctx.companyTimezone
    ),
    endUtc: tenantLocalToUtcDate(
      ctx.dateString,
      endTenantHHMM,
      ctx.companyTimezone
    ),
    startTenantHHMM,
    endTenantHHMM,
    driveMinutesIn,
    driveMilesIn,
    isFixed: true,
  };
}

/**
 * Pre-validate the inputs before paying for any Directions calls.
 */
function preValidate(
  stops: BulkStopDraft[],
  ctx: BulkRouteContext
): BulkRouteConflict[] {
  const conflicts: BulkRouteConflict[] = [];

  // Holiday / day-off short-circuit: no point validating stops if the day
  // itself is off-limits. Surface the most specific reason first.
  if (ctx.isHoliday) {
    conflicts.push({
      stopId: null,
      kind: 'day_is_holiday',
      message: ctx.holidayName
        ? `${ctx.holidayName} is a company holiday - bulk-routing is disabled for this day.`
        : 'This day is a company holiday - pick a different day to bulk-route stops.',
      fixHint: 'Pick a different day',
    });
    return conflicts;
  }
  if (ctx.isDayOff) {
    conflicts.push({
      stopId: null,
      kind: 'day_off',
      message:
        'This day is marked as a day off - pick a working day to bulk-route stops.',
      fixHint: 'Pick a working day or clear the day-off override',
    });
    return conflicts;
  }

  if (stops.length === 0) {
    conflicts.push({
      stopId: null,
      kind: 'no_stops',
      message: 'Add at least one stop to start optimizing.',
    });
    return conflicts;
  }

  if (!ctx.startingCoords) {
    conflicts.push({
      stopId: null,
      kind: 'no_starting_location',
      message: 'Set a starting location for the day before routemizing.',
    });
  }
  if (!ctx.endingCoords) {
    conflicts.push({
      stopId: null,
      kind: 'no_ending_location',
      message: 'Set an ending location for the day before routemizing.',
    });
  }

  const dayStart = hhmmToMinutes(ctx.workingHoursStart);
  const dayEnd = hhmmToMinutes(ctx.workingHoursEnd);
  const earliest = earliestStartMinutes(ctx, stops);

  // Day already over (today, past work hours).
  if (earliest >= dayEnd) {
    conflicts.push({
      stopId: null,
      kind: 'day_in_past',
      message:
        "The selected day's work hours have already ended - pick a future day instead.",
    });
    return conflicts;
  }

  for (const stop of stops) {
    if (!stop.title.trim()) {
      conflicts.push({
        stopId: stop.id,
        kind: 'missing_title',
        message: 'Give this stop a name.',
      });
    }
    if (!stop.durationMinutes || stop.durationMinutes <= 0) {
      conflicts.push({
        stopId: stop.id,
        kind: 'missing_duration',
        message: 'Set a duration for this stop.',
      });
    }
    if (
      stop.latitude == null ||
      stop.longitude == null ||
      !stop.addressLine1.trim()
    ) {
      conflicts.push({
        stopId: stop.id,
        kind: 'address_invalid',
        message: 'Pick an address from the suggestions.',
      });
    }

    // Soft service-area warning. Only zipcode-mode is enforced today (radius
    // mode would need additional Haversine work); empty configs no-op.
    if (
      ctx.serviceArea?.mode === 'zipcode' &&
      ctx.serviceArea.zipCodes.size > 0 &&
      stop.zipCode &&
      stop.zipCode.trim().length > 0
    ) {
      const saCountry = ctx.serviceArea.country;
      const normalized = normalizeServiceAreaCode(String(stop.zipCode), saCountry);
      if (isValidServiceAreaCode(String(stop.zipCode), saCountry) && !ctx.serviceArea.zipCodes.has(normalized)) {
        conflicts.push({
          stopId: stop.id,
          kind: 'service_area_warning',
          severity: 'warning',
          message: `${normalized} is outside your configured service area.`,
          fixHint: 'Schedule anyway or pick a closer address',
        });
      }
    }

    if (stop.fixedStartTime) {
      const start = hhmmToMinutes(stop.fixedStartTime);
      const end = start + stop.durationMinutes + stop.bufferMinutes;
      if (start < dayStart || end > dayEnd) {
        conflicts.push({
          stopId: stop.id,
          kind: 'fixed_time_outside_hours',
          message: `This start time is outside work hours (${formatTimeString(ctx.workingHoursStart)} – ${formatTimeString(ctx.workingHoursEnd)}).`,
          fixHint: 'Make it flexible or adjust work hours',
        });
      }
      if (start < earliest && earliest > dayStart) {
        // Only flag as past when "now" actually clamped the start - skip
        // when earliest === dayStart (future days).
        conflicts.push({
          stopId: stop.id,
          kind: 'fixed_time_in_past',
          message: 'This start time has already passed.',
          fixHint: 'Make it flexible or pick a later time',
        });
      }
      if (
        ctx.lunchStart &&
        ctx.lunchEnd &&
        intervalsOverlap(
          start,
          end,
          hhmmToMinutes(ctx.lunchStart),
          hhmmToMinutes(ctx.lunchEnd)
        )
      ) {
        conflicts.push({
          stopId: stop.id,
          kind: 'fixed_time_overlap_lunch',
          message: `This start time overlaps lunch (${formatTimeString(ctx.lunchStart)} – ${formatTimeString(ctx.lunchEnd)}).`,
          fixHint: 'Make it flexible',
        });
      }
      for (const appt of ctx.existingAppointments.filter(
        isSchedulingBlockerAppointment
      )) {
        const { startMinutes: aStart, endMinutes: aEnd } =
          getExistingAppointmentBlockerMinutes(appt, ctx.companyTimezone);
        if (intervalsOverlap(start, end, aStart, aEnd)) {
          conflicts.push({
            stopId: stop.id,
            kind: 'fixed_time_overlap_existing',
            message: `This start time overlaps "${appt.customerName || appt.type || 'an existing appointment'}".`,
            fixHint: 'Make it flexible or pick a free time',
          });
        }
      }
    }
  }

  // Pairwise fixed-time overlap. Identical start times get a more
  // direct message ("can't share start time with...") so the user
  // doesn't have to puzzle over what "overlaps" means - this is the
  // most common version of this conflict.
  const fixed = stops.filter((s) => s.fixedStartTime);
  for (let i = 0; i < fixed.length; i++) {
    for (let j = i + 1; j < fixed.length; j++) {
      const a = fixed[i];
      const b = fixed[j];
      const aStart = hhmmToMinutes(a.fixedStartTime!);
      const aEnd = aStart + a.durationMinutes + a.bufferMinutes;
      const bStart = hhmmToMinutes(b.fixedStartTime!);
      const bEnd = bStart + b.durationMinutes + b.bufferMinutes;
      if (intervalsOverlap(aStart, aEnd, bStart, bEnd)) {
        const sameStart = aStart === bStart;
        conflicts.push({
          stopId: a.id,
          kind: 'fixed_time_overlap_other_stop',
          message: sameStart
            ? `"${b.title || 'Another stop'}" already starts at this time.`
            : `This start time overlaps "${b.title || 'another fixed-time stop'}".`,
          fixHint: sameStart ? 'Pick a different time' : 'Make one of them flexible',
        });
        conflicts.push({
          stopId: b.id,
          kind: 'fixed_time_overlap_other_stop',
          message: sameStart
            ? `"${a.title || 'Another stop'}" already starts at this time.`
            : `This start time overlaps "${a.title || 'another fixed-time stop'}".`,
          fixHint: sameStart ? 'Pick a different time' : 'Make one of them flexible',
        });
      }
    }
  }

  // Unreachable locked stops: when two locks don't time-overlap but
  // there isn't enough drive time between them, the schedule is
  // physically impossible. Use the same Haversine estimator the
  // optimizer's distribution step uses (with the driving multiplier)
  // so the error message matches what the optimizer would actually
  // attempt. Only check pairs where BOTH stops have geocoded
  // coordinates - rows still being typed are ignored.
  const fixedSorted = [...fixed].sort(
    (a, b) =>
      hhmmToMinutes(a.fixedStartTime!) - hhmmToMinutes(b.fixedStartTime!)
  );
  for (let i = 0; i < fixedSorted.length - 1; i++) {
    const a = fixedSorted[i];
    const b = fixedSorted[i + 1];
    if (
      a.latitude == null ||
      a.longitude == null ||
      b.latitude == null ||
      b.longitude == null
    ) {
      continue;
    }
    const aStart = hhmmToMinutes(a.fixedStartTime!);
    const aEnd = aStart + a.durationMinutes + a.bufferMinutes;
    const bStart = hhmmToMinutes(b.fixedStartTime!);
    const gapMinutes = bStart - aEnd;
    if (gapMinutes < 0) continue; // already caught by overlap check above
    const miles =
      haversineMiles(
        { lat: a.latitude, lng: a.longitude },
        { lat: b.latitude, lng: b.longitude }
      ) * HAVERSINE_DRIVING_MULTIPLIER;
    const requiredDriveMinutes = estimateDriveMinutesFromMiles(miles);
    // 1-minute slack absorbs trivial rounding; anything wider than
    // that is a real impossibility we should refuse to ignore.
    if (requiredDriveMinutes > gapMinutes + 1) {
      const requiredLabel = formatMinutesAsHrMinPure(
        Math.ceil(requiredDriveMinutes)
      );
      const gapLabel =
        gapMinutes <= 0
          ? 'no time'
          : `only ${formatMinutesAsHrMinPure(gapMinutes)}`;
      conflicts.push({
        stopId: a.id,
        kind: 'fixed_time_unreachable',
        message: `Can't reach "${b.title || 'the next fixed-time stop'}" in time - it's about ${requiredLabel} away but you've left ${gapLabel} between them.`,
        fixHint: 'Make one flexible or move it later',
      });
      conflicts.push({
        stopId: b.id,
        kind: 'fixed_time_unreachable',
        message: `Starts too soon after "${a.title || 'the previous fixed-time stop'}" - it's about ${requiredLabel} away but you've left ${gapLabel} between them.`,
        fixHint: 'Make one flexible or move it later',
      });
    }
  }

  return conflicts;
}

/**
 * Main entry point. Validates the draft set, runs Google Directions across
 * each free segment, walks the day to assign times, and returns either a
 * valid plan or a list of conflicts the user can fix.
 */
export async function validateAndOptimize(
  stops: BulkStopDraft[],
  ctx: BulkRouteContext,
  options: { computeAlternative?: boolean } = {}
): Promise<{
  result?: BulkRouteResult;
  conflicts: BulkRouteConflict[];
  /**
   * A "relaxed window" alternative when the user's preferred start/end
   * constraints forced stops to bump past a locked-time stop. Only
   * present when (a) primary run had bumps, (b) user had preferences,
   * and (c) the relaxed run is materially better. The review step
   * surfaces this so users can one-click swap to it.
   */
  alternative?: BulkRouteAlternative;
}> {
  const { computeAlternative = true } = options;
  const preConflicts = preValidate(stops, ctx);
  // Stop early on any HARD error. Warnings (e.g. service-area) are passed
  // through alongside the result so the UI can surface them post-optimize.
  if (hasBlockingConflict(preConflicts)) {
    return { conflicts: preConflicts };
  }
  // Carry warnings forward so they appear next to the final review.
  const warnings = preConflicts.filter(
    (c) => (c.severity ?? 'error') === 'warning'
  );

  // Build blockers: existing appointments + fixed stops + lunch.
  // Track routing coords chronologically so virtual / external events
  // without lat/lng inherit the prior physical stop (same convention as
  // the timeline) instead of snapping segments back to the day start.
  const blockers: DayBlocker[] = [];
  let routingCoords = ctx.startingCoords;
  const schedulingAppointments = ctx.existingAppointments
    .filter(isSchedulingBlockerAppointment)
    .sort(
      (a, b) =>
        appointmentDateToTenantMinutes(a.startDateTime, ctx.companyTimezone) -
        appointmentDateToTenantMinutes(b.startDateTime, ctx.companyTimezone)
    );
  for (const appt of schedulingAppointments) {
    const blocker = appointmentToBlocker(
      appt,
      ctx.companyTimezone,
      routingCoords
    );
    blockers.push(blocker);
    routingCoords = blocker.endCoords ?? routingCoords;
  }
  for (const stop of stops
    .filter((s) => s.fixedStartTime)
    .sort(
      (a, b) =>
        hhmmToMinutes(a.fixedStartTime!) - hhmmToMinutes(b.fixedStartTime!)
    )) {
    const blocker = fixedStopToBlocker(stop, routingCoords);
    blockers.push(blocker);
    routingCoords = blocker.endCoords ?? routingCoords;
  }
  if (ctx.lunchStart && ctx.lunchEnd) {
    const lunchBreakAppt = ctx.existingAppointments.find(
      (a) => a.id === 'lunch-break'
    );
    const lunchCoords =
      lunchBreakAppt?.latitude != null && lunchBreakAppt?.longitude != null
        ? { lat: lunchBreakAppt.latitude, lng: lunchBreakAppt.longitude }
        : null;
    const lunchRouting = lunchCoords ?? routingCoords;
    blockers.push({
      startMinutes: hhmmToMinutes(ctx.lunchStart),
      endMinutes: hhmmToMinutes(ctx.lunchEnd),
      startCoords: lunchRouting,
      endCoords: lunchRouting,
      source: 'lunch',
      label: 'Lunch',
    });
    routingCoords = lunchRouting;
  }

  const segments = buildSegments(ctx, blockers, stops);
  const flexible = stops.filter((s) => !s.fixedStartTime);

  if (segments.length === 0 && flexible.length > 0) {
    return {
      conflicts: [
        {
          stopId: null,
          kind: 'day_overflow',
          message:
            "Your day is fully blocked - there's no free time to fit additional stops.",
        },
      ],
    };
  }

  const { perSegment, unfit, blockerSegmentRejections } =
    distributeFlexibleStops(flexible, segments);

  // Walk each segment in chronological order. When a segment ends at a
  // blocker and pops trailing stops because the trailing drive doesn't
  // fit (or pre-distribute already pushed stops past), we re-fit those
  // stops into LATER segments before walking them. This is what makes
  // a too-tight locked-time stop become the FIRST appointment of the
  // day instead of erroring out.
  let totalDriveMinutes = 0;
  let totalMiles = 0;
  const placedBySegment: BulkStopPlanned[][] = [];
  const overflow: BulkStopDraft[] = [];
  // Tracks which stops got bumped past which blocker so we can surface
  // a clear warning to the user instead of silently reordering.
  const bumpedPastBlocker: { stop: BulkStopDraft; segmentIndex: number }[] = [
    ...blockerSegmentRejections,
  ];
  // Mutable per-segment stop lists - we append rebalanced stops here
  // before optimizing each segment.
  const segmentStops: BulkStopDraft[][] = perSegment.map((s) => [...s]);
  // Stops that couldn't be distributed, plus any popped during walking,
  // wait here to be re-fit into later segments.
  let rebalancePool: BulkStopDraft[] = [...unfit];

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    // Try to pull each rebalance-pool stop into THIS segment. We use
    // the same Haversine-based fit check as the initial distribute, but
    // we don't penalize the trailing drive for the LAST segment of the
    // day (there's no blocker to race to).
    const stillPooled: BulkStopDraft[] = [];
    let segBudget = seg.endMinutes - seg.startMinutes;
    // Subtract already-allocated time (including drive) so we don't over-pack.
    for (const s of segmentStops[i]) {
      segBudget -= estimateFlexibleStopSegmentCost(s, seg);
    }
    for (const stop of rebalancePool) {
      if (stop.latitude == null || stop.longitude == null) {
        stillPooled.push(stop);
        continue;
      }
      const estCost = estimateFlexibleStopSegmentCost(stop, seg);
      if (estCost <= segBudget) {
        segmentStops[i].push(stop);
        segBudget -= estCost;
      } else {
        stillPooled.push(stop);
      }
    }
    rebalancePool = stillPooled;

    const r = await optimizeSegmentOrder(seg, segmentStops[i]);
    const walked = walkSegment(ctx, seg, r.orderedStops, r.legs);
    placedBySegment.push(walked.placed);
    totalDriveMinutes += walked.placed.reduce(
      (sum, p) => sum + p.driveMinutesIn,
      0
    );
    totalMiles += walked.segmentMiles;
    // Stops popped here go back into the rebalance pool so the next
    // segment can attempt them. Stops popped from a blocker-ending
    // segment are also recorded for the warning.
    if (walked.overflow.length > 0) {
      rebalancePool.push(...walked.overflow);
      if (seg.endsAtBlocker) {
        for (const popped of walked.overflow) {
          bumpedPastBlocker.push({ stop: popped, segmentIndex: i });
        }
      }
    }
  }

  // Anything still in the pool after the last segment is a true overflow.
  overflow.push(...rebalancePool);

  if (overflow.length > 0) {
    const overflowConflicts: BulkRouteConflict[] = overflow.map((s) => ({
      stopId: s.id,
      kind: 'day_overflow',
      message: `Not enough open time in the day to fit "${s.title || 'this stop'}".`,
      fixHint: 'Trim duration, remove a stop, or extend work hours',
    }));
    return { conflicts: overflowConflicts };
  }

  // Assemble the chronological ordered list, weaving in fixed-time stops.
  const sortedFixed = stops
    .filter((s) => s.fixedStartTime)
    .sort(
      (a, b) =>
        hhmmToMinutes(a.fixedStartTime!) - hhmmToMinutes(b.fixedStartTime!)
    );

  const orderedStops: BulkStopPlanned[] = [];
  let prevCoords: Coordinates = ctx.startingCoords;

  // Walk the day chronologically. Segments and fixed stops alternate (with
  // existing appointments interleaved as context anchors).
  // Strategy: collect all placed (segment + fixed) into one list, sort by
  // tenant-local start minutes, then compute drive-in legs based on previous
  // coords for each.
  const allPlaced: BulkStopPlanned[] = [];
  for (const seg of placedBySegment) allPlaced.push(...seg);
  for (const fixed of sortedFixed) {
    allPlaced.push(placedFromFixedDraft(ctx, fixed, 0, 0));
  }
  allPlaced.sort(
    (a, b) =>
      hhmmToMinutes(a.startTenantHHMM) - hhmmToMinutes(b.startTenantHHMM)
  );

  // Recompute drive-in for each (so fixed stops get a real leg from prior).
  for (const placed of allPlaced) {
    const stopCoords = {
      lat: placed.draft.latitude!,
      lng: placed.draft.longitude!,
    };
    if (placed.isFixed) {
      const miles =
        haversineMiles(prevCoords, stopCoords) * HAVERSINE_DRIVING_MULTIPLIER;
      placed.driveMilesIn = miles;
      placed.driveMinutesIn = estimateDriveMinutesFromMiles(miles);
      totalMiles += miles;
      totalDriveMinutes += placed.driveMinutesIn;
    }
    prevCoords = stopCoords;
    orderedStops.push(placed);
  }

  // Final drive home leg.
  const { finalLeg, endMinutes: lastEndMinutes } = finishDay(
    ctx,
    orderedStops[orderedStops.length - 1],
    () => haversineLeg(0, prevCoords, ctx.endingCoords)
  );
  const { miles: finalLegMiles, minutes: finalLegMinutes } = finalLeg;
  totalMiles += finalLegMiles;
  totalDriveMinutes += finalLegMinutes;

  const totalAtStopMinutes = orderedStops.reduce(
    (sum, p) => sum + p.draft.durationMinutes,
    0
  );
  const totalBufferMinutes = orderedStops.reduce(
    (sum, p) => sum + p.draft.bufferMinutes,
    0
  );

  const contextAnchors: ContextAnchor[] = ctx.existingAppointments
    .filter(isSchedulingBlockerAppointment)
    .map((appt) => ({
      kind: 'existing_appointment' as const,
      appointmentId: appt.id,
      startTenantHHMM: appointmentDateToTenantHHMM(
        appt.startDateTime,
        ctx.companyTimezone
      ),
      endTenantHHMM: appointmentDateToTenantHHMM(
        appt.endDateTime,
        ctx.companyTimezone
      ),
      label: appt.customerName || appt.type || 'Existing appointment',
      bufferBeforeMinutes: appt.preBufferMinutes ?? 0,
      bufferAfterMinutes: appt.postBufferMinutes ?? 0,
    }));
  if (ctx.lunchStart && ctx.lunchEnd) {
    contextAnchors.push({
      kind: 'lunch',
      startTenantHHMM: ctx.lunchStart,
      endTenantHHMM: ctx.lunchEnd,
      label: 'Lunch',
    });
  }

  const result: BulkRouteResult = {
    orderedStops,
    contextAnchors,
    totalMiles,
    totalDriveMinutes,
    totalAtStopMinutes,
    totalBufferMinutes,
    endingTimeHHMM: minutesToHHMM(lastEndMinutes),
    finalDriveMinutes: finalLegMinutes,
    finalDriveMiles: finalLegMiles,
  };

  // Compute a "relaxed window" alternative when:
  //   1. The user gave us a preferred start or end time (something to relax)
  //   2. Stops were bumped past a blocker (the constraint actually mattered)
  //   3. The primary run is finalized (skip recursive computation)
  // The alternative is the same optimization with no preferred window.
  // We only return it when it's materially better than the primary so
  // the review step can offer a one-tap "use this instead" option.
  let alternative: BulkRouteAlternative | undefined;
  if (
    computeAlternative &&
    bumpedPastBlocker.length > 0 &&
    (ctx.preferredStartTime || ctx.preferredEndTime)
  ) {
    const relaxedCtx: BulkRouteContext = {
      ...ctx,
      preferredStartTime: undefined,
      preferredEndTime: undefined,
    };
    try {
      const altRun = await validateAndOptimize(stops, relaxedCtx, {
        computeAlternative: false,
      });
      if (altRun.result && altRun.result.orderedStops.length > 0) {
        const milesSaved = totalMiles - altRun.result.totalMiles;
        const driveMinutesSaved =
          totalDriveMinutes - altRun.result.totalDriveMinutes;
        const sameStopCount =
          altRun.result.orderedStops.length === orderedStops.length;
        // Material threshold: at least 1 mile or 5 minutes saved, and
        // we must place the same number of stops (we'd never suggest
        // an alternative that drops stops).
        if (
          sameStopCount &&
          (milesSaved > 1 || driveMinutesSaved > 5)
        ) {
          // Use the alternative's actual first/last stop times as the
          // suggestion. If they match the workday hours, we surface
          // `null` so the UI can show "use full workday" instead of a
          // specific time.
          const altFirst = altRun.result.orderedStops[0];
          const altLast =
            altRun.result.orderedStops[
              altRun.result.orderedStops.length - 1
            ];
          const altStartHHMM = altFirst.startTenantHHMM;
          const altEndMinutes =
            hhmmToMinutes(altLast.endTenantHHMM) +
            altLast.draft.bufferMinutes;
          const altEndHHMM = minutesToHHMM(altEndMinutes);
          const userStartM = ctx.preferredStartTime
            ? hhmmToMinutes(ctx.preferredStartTime)
            : null;
          const userEndM = ctx.preferredEndTime
            ? hhmmToMinutes(ctx.preferredEndTime)
            : null;
          alternative = {
            suggestedStartTime:
              userStartM != null &&
              hhmmToMinutes(altStartHHMM) < userStartM
                ? altStartHHMM
                : null,
            suggestedEndTime:
              userEndM != null && altEndMinutes > userEndM
                ? altEndHHMM
                : null,
            result: altRun.result,
            savings: {
              miles: Math.max(0, milesSaved),
              driveMinutes: Math.max(0, driveMinutesSaved),
            },
          };
        }
      }
    } catch {
      // Alternative is best-effort; never let a failure here break the
      // primary route. Swallow and proceed.
    }
  }

  // Emit a friendly warning when stops were bumped past a blocker (most
  // commonly a locked-time stop) because the drive time before it
  // didn't allow them. This explains to the user WHY the locked stop
  // ended up first / why their typed order changed.
  if (bumpedPastBlocker.length > 0) {
    // Group rejections by the blocker that caused them. The blocker
    // following segment index N is the (N+1)th sorted blocker.
    const sortedBlockers = [...blockers].sort(
      (a, b) => a.startMinutes - b.startMinutes
    );
    const seenSegments = new Set<number>();
    for (const { segmentIndex } of bumpedPastBlocker) {
      if (seenSegments.has(segmentIndex)) continue;
      seenSegments.add(segmentIndex);
      const blocker = sortedBlockers[segmentIndex];
      if (!blocker || blocker.source === 'lunch') continue;
      const stopsForThisBlocker = bumpedPastBlocker
        .filter((r) => r.segmentIndex === segmentIndex)
        .map((r) => r.stop);
      const blockerLabel =
        blocker.source === 'fixed_stop'
          ? `"${blocker.fixedDraft?.title || 'a fixed-time stop'}"`
          : `"${blocker.label}"`;
      const blockerTime = formatHHMMto12hPure(
        minutesToHHMM(blocker.startMinutes)
      );
      const otherCount = stopsForThisBlocker.length;
      warnings.push({
        stopId: null,
        kind: 'lock_anchored_first',
        severity: 'warning',
        message:
          otherCount === 1
            ? `Routemize moved ${blockerLabel} to run before "${stopsForThisBlocker[0].title || 'another stop'}" - the ${blockerTime} time didn't leave enough drive time to fit it earlier.`
            : `Routemize moved ${blockerLabel} ahead of ${otherCount} other stops - the ${blockerTime} time didn't leave enough drive time to fit them earlier.`,
      });
    }
  }

  return { conflicts: warnings, result, alternative };
}

/**
 * Internal helper to format an HH:MM string as 12-hour AM/PM. Kept
 * separate from any imported `formatHHMMto12h` to avoid coupling the
 * optimizer to display-layer utilities.
 */
function formatHHMMto12hPure(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

/**
 * Internal helper to format a minute count as a human-readable
 * "1 hr 40 min" string. Used in conflict messages so users don't
 * have to mentally divide larger numbers (e.g. "114 min" → "1 hr
 * 54 min"). Mirrors the same shape as the modal's formatter.
 */
function formatMinutesAsHrMinPure(totalMinutes: number): string {
  if (totalMinutes <= 0) return '0 min';
  const h = Math.floor(totalMinutes / 60);
  const m = Math.round(totalMinutes % 60);
  if (h === 0) return `${m} min`;
  if (m === 0) return h === 1 ? '1 hr' : `${h} hr`;
  return `${h} hr ${m} min`;
}

/**
 * Cheap re-walk after a manual reorder in the review step. Recomputes drive
 * legs via Haversine (no Google call) so the user gets instant feedback.
 * Fixed-time stops, existing appointments and lunch stay put; flexible
 * stops are re-spaced around them.
 */
/** Drive miles/minutes for one leg, from whatever data source is walking the order. */
interface WalkLeg {
  miles: number;
  minutes: number;
}

type WalkFailureReason = 'fixed_out_of_order' | 'fixed_time_unreachable' | 'day_overflow';

interface WalkFailure {
  ok: false;
  reason: WalkFailureReason;
  stopId: string | null;
}

interface WalkSuccess {
  ok: true;
  value: BulkRouteResult;
}

function plannedArrival(draft: BulkStopDraft, cursor: number, leg: WalkLeg): number {
  // Flexible stops snap to a 5-minute grid for tidy display.
  return draft.fixedStartTime
    ? hhmmToMinutes(draft.fixedStartTime)
    : roundUpToNearest5(cursor + leg.minutes);
}

/**
 * When the day wraps up: after the last bulk stop, or after an existing
 * appointment that starts later, whichever ends last. The drive home
 * leaves from that appointment when it has a location.
 */
function finishDay(
  ctx: BulkRouteContext,
  lastStop: BulkStopPlanned | undefined,
  homeLegFromLastStop: () => WalkLeg
): { finalLeg: WalkLeg; endMinutes: number } {
  if (!lastStop) {
    return {
      finalLeg: homeLegFromLastStop(),
      endMinutes: hhmmToMinutes(ctx.workingHoursStart),
    };
  }
  const lastDeparture =
    hhmmToMinutes(lastStop.endTenantHHMM) + lastStop.draft.bufferMinutes;
  let finish = lastDeparture;
  let homeFrom: Coordinates | null = null;
  for (const blocker of contextBlockers(ctx)) {
    if (blocker.source !== 'existing_appointment') continue;
    if (blocker.startMinutes < lastDeparture) continue;
    finish = Math.max(finish, blocker.endMinutes);
    if (blocker.endCoords) homeFrom = blocker.endCoords;
  }
  const finalLeg = homeFrom
    ? haversineLeg(0, homeFrom, ctx.endingCoords)
    : homeLegFromLastStop();
  return { finalLeg, endMinutes: roundUpToNearest5(finish + finalLeg.minutes) };
}

/**
 * Shared placement walk used by both `rewalkOrder` (Haversine estimate,
 * synchronous) and the real-drive-time re-validation in
 * `refineLegsWithDirections`. `legForStop`/`legForFinal` are the only
 * difference between the two callers - everything else (fixed-time pinning,
 * routing around existing appointments and lunch, 5-minute rounding,
 * overflow checks, context anchors) is identical so the two paths can't
 * drift apart.
 */
function walkOrderedStops(
  orderedDrafts: BulkStopDraft[],
  ctx: BulkRouteContext,
  legForStop: (index: number, prevCoords: Coordinates, stopCoords: Coordinates) => WalkLeg,
  legForFinal: (prevCoords: Coordinates, endingCoords: Coordinates) => WalkLeg
): WalkSuccess | WalkFailure {
  // Verify fixed stops are still in chronological order.
  const fixedTimes: number[] = [];
  for (const d of orderedDrafts) {
    if (d.fixedStartTime) {
      const t = hhmmToMinutes(d.fixedStartTime);
      if (fixedTimes.length > 0 && t < fixedTimes[fixedTimes.length - 1]) {
        return { ok: false, reason: 'fixed_out_of_order', stopId: d.id };
      }
      fixedTimes.push(t);
    }
  }

  const orderedStops: BulkStopPlanned[] = [];
  let cursor = earliestStartMinutes(ctx, orderedDrafts);
  const dayEnd = latestEndMinutes(ctx, orderedDrafts);
  let prevCoords: Coordinates = ctx.startingCoords;
  let totalMiles = 0;
  let totalDriveMinutes = 0;
  const blockers = contextBlockers(ctx);
  let nextBlocker = 0;

  for (const [index, draft] of orderedDrafts.entries()) {
    const stopCoords = {
      lat: draft.latitude!,
      lng: draft.longitude!,
    };
    let leg = legForStop(index, prevCoords, stopCoords);
    let arrival = plannedArrival(draft, cursor, leg);

    // Existing appointments and lunch don't move. When this stop can't
    // finish and drive on before the next one starts, go through that
    // blocker first and resume from where and when it ends.
    for (; nextBlocker < blockers.length; nextBlocker++) {
      const blocker = blockers[nextBlocker];
      if (blocker.endMinutes <= cursor) continue;
      const departure = arrival + draft.durationMinutes + draft.bufferMinutes;
      const driveOn = blocker.startCoords
        ? haversineLeg(index, stopCoords, blocker.startCoords).minutes
        : 0;
      if (departure + driveOn <= blocker.startMinutes) break;
      // A pinned time inside a blocker is already a validation error; keep the pin.
      if (draft.fixedStartTime && arrival < blocker.endMinutes) break;
      cursor = Math.max(cursor, blocker.endMinutes);
      if (blocker.endCoords) {
        prevCoords = blocker.endCoords;
        // Real-drive legs only cover stop-to-stop hops, so estimate from the blocker.
        leg = haversineLeg(index, prevCoords, stopCoords);
      }
      arrival = plannedArrival(draft, cursor, leg);
    }

    const { miles, minutes: driveMinutes } = leg;
    if (draft.fixedStartTime && arrival < cursor + driveMinutes) {
      return { ok: false, reason: 'fixed_time_unreachable', stopId: draft.id };
    }

    const departure = arrival + draft.durationMinutes + draft.bufferMinutes;
    if (departure > dayEnd) {
      return { ok: false, reason: 'day_overflow', stopId: draft.id };
    }

    const startHHMM = minutesToHHMM(arrival);
    const endHHMM = minutesToHHMM(arrival + draft.durationMinutes);
    orderedStops.push({
      draft,
      startUtc: tenantLocalToUtcDate(
        ctx.dateString,
        startHHMM,
        ctx.companyTimezone
      ),
      endUtc: tenantLocalToUtcDate(
        ctx.dateString,
        endHHMM,
        ctx.companyTimezone
      ),
      startTenantHHMM: startHHMM,
      endTenantHHMM: endHHMM,
      driveMinutesIn: driveMinutes,
      driveMilesIn: miles,
      isFixed: !!draft.fixedStartTime,
    });

    totalMiles += miles;
    totalDriveMinutes += driveMinutes;
    cursor = departure;
    prevCoords = stopCoords;
  }

  const { finalLeg, endMinutes: lastEndMinutes } = finishDay(
    ctx,
    orderedStops[orderedStops.length - 1],
    () => legForFinal(prevCoords, ctx.endingCoords)
  );
  const { miles: finalLegMiles, minutes: finalLegMinutes } = finalLeg;
  totalMiles += finalLegMiles;
  totalDriveMinutes += finalLegMinutes;

  const totalAtStopMinutes = orderedStops.reduce(
    (sum, p) => sum + p.draft.durationMinutes,
    0
  );
  const totalBufferMinutes = orderedStops.reduce(
    (sum, p) => sum + p.draft.bufferMinutes,
    0
  );

  const contextAnchors: ContextAnchor[] = ctx.existingAppointments
    .filter(isSchedulingBlockerAppointment)
    .map((appt) => ({
      kind: 'existing_appointment' as const,
      appointmentId: appt.id,
      startTenantHHMM: appointmentDateToTenantHHMM(
        appt.startDateTime,
        ctx.companyTimezone
      ),
      endTenantHHMM: appointmentDateToTenantHHMM(
        appt.endDateTime,
        ctx.companyTimezone
      ),
      label: appt.customerName || appt.type || 'Existing appointment',
      bufferBeforeMinutes: appt.preBufferMinutes ?? 0,
      bufferAfterMinutes: appt.postBufferMinutes ?? 0,
    }));
  if (ctx.lunchStart && ctx.lunchEnd) {
    contextAnchors.push({
      kind: 'lunch',
      startTenantHHMM: ctx.lunchStart,
      endTenantHHMM: ctx.lunchEnd,
      label: 'Lunch',
    });
  }

  return {
    ok: true,
    value: {
      orderedStops,
      contextAnchors,
      totalMiles,
      totalDriveMinutes,
      totalAtStopMinutes,
      totalBufferMinutes,
      endingTimeHHMM: minutesToHHMM(lastEndMinutes),
      finalDriveMinutes: finalLegMinutes,
      finalDriveMiles: finalLegMiles,
    },
  };
}

function haversineLeg(_index: number, a: Coordinates, b: Coordinates): WalkLeg {
  const miles = haversineMiles(a, b) * HAVERSINE_DRIVING_MULTIPLIER;
  return { miles, minutes: estimateDriveMinutesFromMiles(miles) };
}

export function rewalkOrder(
  orderedDrafts: BulkStopDraft[],
  ctx: BulkRouteContext
): BulkRouteResult | null {
  const walked = walkOrderedStops(orderedDrafts, ctx, haversineLeg, (a, b) =>
    haversineLeg(0, a, b)
  );
  return walked.ok ? walked.value : null;
}

function conflictForWalkFailure(failure: WalkFailure): BulkRouteConflict {
  switch (failure.reason) {
    case 'fixed_time_unreachable':
      return {
        stopId: failure.stopId,
        kind: 'fixed_time_unreachable',
        message:
          "Real drive times make this order impossible - a fixed-time stop can't be reached in time. Re-optimize or move it.",
      };
    case 'day_overflow':
      return {
        stopId: failure.stopId,
        kind: 'day_overflow',
        message:
          'Real drive times push this order past the end of the day. Re-optimize or remove a stop.',
      };
    case 'fixed_out_of_order':
    default:
      return {
        stopId: failure.stopId,
        kind: 'fixed_time_unreachable',
        message:
          "Real drive times conflict with a fixed-time stop's order. Re-optimize to fix it.",
      };
  }
}

/**
 * Re-query Google Directions for the EXACT current ordering (no
 * optimization) and RE-WALK the placement using the real Google leg
 * durations (not Haversine). Used post-reorder/remove so the times that
 * actually get saved reflect real driving, not the optimistic Haversine
 * estimate `rewalkOrder` used for the immediate (synchronous) preview.
 *
 * When the real drive times still fit the day and all locks, returns the
 * fully re-timed result. When they don't (a lock becomes unreachable, or
 * the day overflows), the optimistic times are NOT applied silently -
 * only the display miles/minutes are patched (so the user can see why),
 * and a blocking conflict is returned for the caller to surface and use to
 * prevent saving.
 *
 * Falls back to the original result (no conflict) on any Directions error
 * so the user is never left without something to look at.
 */
export async function refineLegsWithDirections(
  result: BulkRouteResult,
  ctx: BulkRouteContext
): Promise<{ result: BulkRouteResult; conflict: BulkRouteConflict | null }> {
  if (result.orderedStops.length === 0) return { result, conflict: null };

  const waypoints: Coordinates[] = result.orderedStops.map((s) => ({
    lat: s.draft.latitude!,
    lng: s.draft.longitude!,
  }));

  try {
    // We pass the full waypoint list and force `optimizeWaypoints: false`
    // semantics by relying on calculateOptimizedRoute - it already returns
    // both the current-order legs and the optimized-order legs; we use the
    // current-order data here.
    const optimized = await googleMapsDistanceService.calculateOptimizedRoute(
      ctx.startingCoords,
      ctx.endingCoords,
      waypoints
    );
    if (!optimized) return { result, conflict: null };

    const currentLegs = optimized.currentLegs;
    if (currentLegs.length !== result.orderedStops.length + 1) {
      return { result, conflict: null };
    }

    const orderedDrafts = result.orderedStops.map((s) => s.draft);
    const realLegForStop = (index: number): WalkLeg => ({
      miles: currentLegs[index].distance,
      minutes: currentLegs[index].duration,
    });
    const realLegForFinal = (): WalkLeg => ({
      miles: currentLegs[orderedDrafts.length].distance,
      minutes: currentLegs[orderedDrafts.length].duration,
    });

    const rewalked = walkOrderedStops(
      orderedDrafts,
      ctx,
      realLegForStop,
      realLegForFinal
    );

    if (rewalked.ok) {
      return { result: rewalked.value, conflict: null };
    }

    // Real drive time broke feasibility. Patch the display numbers (miles/
    // minutes) so the UI reflects reality, but do NOT recompute start/end
    // times from a walk that failed - keep the last-known-good times and
    // let the returned conflict block saving until the user re-optimizes.
    let totalMiles = 0;
    let totalDriveMinutes = 0;
    const patchedStops: BulkStopPlanned[] = result.orderedStops.map(
      (s, i) => {
        const leg = currentLegs[i];
        totalMiles += leg.distance;
        totalDriveMinutes += leg.duration;
        return {
          ...s,
          driveMilesIn: leg.distance,
          driveMinutesIn: leg.duration,
        };
      }
    );
    const finalLeg = currentLegs[orderedDrafts.length];
    totalMiles += finalLeg.distance;
    totalDriveMinutes += finalLeg.duration;

    return {
      result: {
        ...result,
        orderedStops: patchedStops,
        totalMiles,
        totalDriveMinutes,
        finalDriveMiles: finalLeg.distance,
        finalDriveMinutes: finalLeg.duration,
      },
      conflict: conflictForWalkFailure(rewalked),
    };
  } catch {
    return { result, conflict: null };
  }
}
